'use client'

import { useState, useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { useTranslation } from '@/i18n/useTranslation'
import { langPrefix } from '@/i18n/translations'
import { usePathLang } from '@/hooks/usePathLang'
import { stripLocalePrefix } from '@/utils/breadcrumbTrail'
import AirestroAnnouncementBar from './AirestroAnnouncementBar'
import AirestroHeaderNav from './AirestroHeaderNav'
import Footer from './Footer'
import { RESTRO_LOGIN_URL, RESTRO_REGISTER_URL } from '@/config/portal'
import '@/styles/airestro-landing.css'

export type CmsNavPage = { id: number; title: string; slug: string; placement?: string }

type Props = {
  children: React.ReactNode
  /** From server layout; omit in not-found to use empty nav (no client CMS round-trip). */
  footerPages?: CmsNavPage[]
  legalVisibility?: Record<string, boolean>
  showFaqLink?: boolean
}

export default function SiteLayoutClient({
  children,
  footerPages = [],
  legalVisibility = {},
  showFaqLink = false,
}: Props) {
  const lang = usePathLang()
  const pathname = usePathname() || '/'
  const t = useTranslation(lang)
  const [headerScrolled, setHeaderScrolled] = useState(false)
  const [scrollY, setScrollY] = useState(0)
  const headerRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang
  document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr'
    }
  }, [lang])

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY
      setScrollY(y)
      setHeaderScrolled(y > 12)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const lp = langPrefix(lang)
  const { rest: pathRest } = stripLocalePrefix(pathname || '/')
  const isMarketingHome = pathRest === '/'

  const showPromoBars = isMarketingHome ? !headerScrolled : scrollY < 1

  /* Fixed home header: keep page content clear of the stack height */
  useEffect(() => {
    if (!isMarketingHome) {
      document.documentElement.style.removeProperty('--ar-home-header-h')
      return
    }
    const el = headerRef.current
    if (!el) return
    const sync = () => {
      document.documentElement.style.setProperty('--ar-home-header-h', `${el.offsetHeight}px`)
    }
    sync()
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(sync) : null
    ro?.observe(el)
    window.addEventListener('resize', sync)
    return () => {
      ro?.disconnect()
      window.removeEventListener('resize', sync)
      document.documentElement.style.removeProperty('--ar-home-header-h')
    }
  }, [isMarketingHome, lang, showPromoBars])

  return (
    <div className={`home-page${isMarketingHome ? ' home-page--marketing-home' : ''}`}>
      <header
        ref={headerRef}
        className={[
          'header',
          'header--ar',
          'ar-header-shell',
          isMarketingHome ? 'ar-header-shell--home' : '',
          headerScrolled ? 'ar-header-shell--scrolled' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {/* Black top bar + green Farori bar: visible at top only; hide on scroll */}
        {showPromoBars ? (
          <AirestroAnnouncementBar t={t} lang={lang} className="ar-announcement-bar--desktop-only" />
        ) : null}
        <div className="ar-header-float-area">
          <AirestroHeaderNav
            t={t}
            lang={lang}
            lp={lp}
            pathname={pathname}
            showAnnouncement={showPromoBars}
            legalVisibility={legalVisibility}
          />
        </div>
        {isMarketingHome && showPromoBars ? (
          <div className="ar-offer-bar" role="region" aria-label={t('landing.cineOfferAria')}>
            <p className="ar-offer-bar__text">
              {t('landing.cineOfferBefore')}{' '}
              <a href={RESTRO_LOGIN_URL} target="_blank" rel="noopener noreferrer">
                {t('landing.cineOfferLogin')}
              </a>{' '}
              {t('landing.cineOfferOr')}{' '}
              <a href={RESTRO_REGISTER_URL} target="_blank" rel="noopener noreferrer">
                {t('landing.cineOfferStart')}
              </a>
            </p>
          </div>
        ) : null}
      </header>

      <main
        id="main-content"
        className={`main cms-main${isMarketingHome ? ' cms-main--home' : ''}`.trim()}
        tabIndex={-1}
      >
        {children}
      </main>

      <Footer
        lang={lang}
        pathname={pathname}
        t={t}
        footerPages={footerPages}
        legalVisibility={legalVisibility}
        showFaqLink={showFaqLink}
      />
    </div>
  )
}
